import { computed, Injectable, signal } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'

import { map } from 'rxjs'

import { classifyBaseError } from 'app/_shared/helpers/base-points.helpers'
import { BasePointsBalance } from 'app/_shared/interfaces/BasePoints'

import { ApiService } from './api.service'

interface BalanceRequest {
  storefrontId: number
  identityKey: string
}

interface BasePointsBalanceResponse {
  points: number
  currency: {
    long_name: string
    short_name: string
    prefix: boolean
  }
}

@Injectable({ providedIn: 'root' })
export class BasePointsService extends ApiService {
  private readonly requestParams = signal<BalanceRequest | undefined>(undefined)

  private readonly balanceResource = rxResource<
    BasePointsBalance | null,
    BalanceRequest | undefined
  >({
    params: () => this.requestParams(),
    stream: ({ params }) =>
      this.get<BasePointsBalance | BasePointsBalanceResponse>(
        `${this.apiUrl}/storefronts/${params.storefrontId}/base-ai/balance`
      ).pipe(
        map(response => {
          const balance = response.data

          if ('points' in balance) {
            return {
              available_points: balance.points,
              currency_long_name: balance.currency.long_name,
              currency_short_name: balance.currency.short_name,
              currency_prefix: balance.currency.prefix
            }
          }

          return balance
        })
      ),
    defaultValue: null
  })

  readonly balance = computed(() =>
    this.balanceResource.hasValue() ? this.balanceResource.value() : null
  )
  readonly isChecking = this.balanceResource.isLoading
  readonly error = computed(() => {
    const error = this.balanceResource.error()
    return error ? classifyBaseError(error) : null
  })
  readonly isReady = computed(() => !!this.balance() && !this.isChecking() && !this.error())

  check(storefrontId: number, identityKey: string, force = false): void {
    const current = this.requestParams()
    if (current?.storefrontId === storefrontId && current.identityKey === identityKey && force) {
      this.balanceResource.reload()
      return
    }

    this.requestParams.set({ storefrontId, identityKey })
  }

  retry(): void {
    this.balanceResource.reload()
  }

  updateAvailablePoints(availablePoints: number): void {
    const current = this.balance()
    if (!current) return

    this.balanceResource.set({
      ...current,
      available_points: availablePoints
    })
  }

  clear(): void {
    this.requestParams.set(undefined)
    this.balanceResource.set(null)
  }
}
